import React, { type ChangeEvent } from "react";
import { IconButton } from "@material-ui/core";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import { t } from "adapters/i18n";
import { BannerAlert } from "features/shared/components";
import type {
  CycleValidationError,
  EditableCyclePeriod,
  ForageCycleRecord,
} from "../../../forageCycleManagementUtils";

export type EditablePeriodField =
  | "AlturaEntrada"
  | "AlturaSaida"
  | "QuantidadeDiasDescanso"
  | "Rotulo"
  | "dataFim"
  | "dataInicio";

interface PeriodFieldDefinition {
  accessibleLabelKey: string;
  field: EditablePeriodField;
  labelKey: string;
  type: "number" | "text";
  unitKey?: string;
  maxWidth?: string;
}

const fieldGroups: Array<{
  className: string;
  fields: PeriodFieldDefinition[];
}> = [
  {
    className: "forage-cycle-period-group--dates",
    fields: [
      {
        field: "dataInicio",
        labelKey: "cycle-start-date",
        accessibleLabelKey: "cycle-start-date-accessible",
        type: "text",
        maxWidth: "46px",
      },
      {
        field: "dataFim",
        labelKey: "cycle-end-date",
        accessibleLabelKey: "cycle-end-date-accessible",
        type: "text",
        maxWidth: "46px",
      },
    ],
  },
  {
    className: "forage-cycle-period-group--heights",
    fields: [
      {
        field: "AlturaEntrada",
        labelKey: "cycle-entry-height",
        accessibleLabelKey: "cycle-entry-height-accessible",
        type: "number",
        unitKey: "cycle-unit-cm",
        maxWidth: "64px",
      },
      {
        field: "AlturaSaida",
        labelKey: "cycle-exit-height",
        accessibleLabelKey: "cycle-exit-height-accessible",
        type: "number",
        unitKey: "cycle-unit-cm",
        maxWidth: "64px",
      },
    ],
  },
  {
    className: "forage-cycle-period-group--rest",
    fields: [
      {
        field: "QuantidadeDiasDescanso",
        labelKey: "cycle-rest-days",
        accessibleLabelKey: "cycle-rest-days-accessible",
        type: "number",
        unitKey: "cycle-unit-days",
      },
    ],
  },
];

function maskDayMonth(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length < 2
    ? digits
    : `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

interface ForageCyclePeriodRowProps {
  cycle: ForageCycleRecord;
  index: number;
  period: EditableCyclePeriod;
  periodCount: number;
  saving: boolean;
  validationErrors: CycleValidationError[];
  onChange: (field: EditablePeriodField, value: string) => void;
  onRemove: () => void;
}

function ForageCyclePeriodRow({
  cycle,
  index,
  period,
  periodCount,
  saving,
  validationErrors,
  onChange,
  onRemove,
}: ForageCyclePeriodRowProps) {
  const forageName =
    cycle.nomeForragem ||
    t("forageCycle:cycle-forage-id", { id: cycle.IdForragem });
  const periodLabel = t("forageCycle:cycle-period-title", {
    number: index + 1,
  });
  const rowErrors = validationErrors.filter((error) => error.index === index);
  const rowErrorMessages = Array.from(
    new Set(rowErrors.map((error) => t(`forageCycle:${error.messageKey}`))),
  );
  const errorDescriptionId = `period-errors-${cycle.IdForragem}-${index}`;

  const renderField = (definition: PeriodFieldDefinition) => {
    const error = rowErrors.find((item) => item.field === definition.field);
    const testId = `period-${cycle.IdForragem}-${index}-${definition.field}`;
    const accessibleLabel = t(`forageCycle:${definition.accessibleLabelKey}`);
    const isDateField =
      definition.field === "dataInicio" || definition.field === "dataFim";
    const isRestField = definition.field === "QuantidadeDiasDescanso";

    return (
      <div
        className={`forage-cycle-period-field${
          error ? " forage-cycle-period-field--error" : ""
        }${isRestField ? " forage-cycle-period-field--stacked" : ""}`}
        data-testid={`${testId}-field`}
        key={definition.field}
      >
        <label className="forage-cycle-period-field__label" htmlFor={testId}>
          {t(`forageCycle:${definition.labelKey}`)}:
        </label>
        <div
          className="forage-cycle-period-field__value"
          style={{ maxWidth: definition.maxWidth ?? "100%" }}
        >
          <input
            className="forage-cycle-input"
            id={testId}
            type={definition.type}
            disabled={saving}
            min={definition.field === "QuantidadeDiasDescanso" ? 1 : undefined}
            maxLength={isDateField ? 5 : undefined}
            inputMode={
              isDateField
                ? "numeric"
                : definition.type === "number"
                  ? "decimal"
                  : "text"
            }
            placeholder={isDateField ? "DD/MM" : undefined}
            aria-label={`${accessibleLabel} - ${forageName} - ${periodLabel}`}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorDescriptionId : undefined}
            data-testid={testId}
            value={period[definition.field] ?? ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange(
                definition.field,
                isDateField
                  ? maskDayMonth(event.target.value)
                  : event.target.value,
              )
            }
          />
          {definition.unitKey && (
            <span className="forage-cycle-unit">
              {t(`forageCycle:${definition.unitKey}`)}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="forage-cycle-period-row"
      data-period-index={index}
      data-testid={`period-card-${cycle.IdForragem}-${index}`}
    >
      <div className="forage-cycle-period-name">
        <div className="forage-cycle-period-name__input">
          <input
            type="text"
            disabled={saving}
            maxLength={undefined}
            aria-label={`${t("forageCycle:cycle-period-label-accessible")} - ${forageName} - ${periodLabel}`}
            data-testid={`period-${cycle.IdForragem}-${index}-Rotulo`}
            value={period.Rotulo}
            placeholder={`Período ${index + 1}`}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange("Rotulo", event.target.value)
            }
          />
        </div>
        <span className="forage-cycle-period-name__editable">
          {t("forageCycle:cycle-period-editable")}
        </span>
      </div>

      {fieldGroups.map((group) => (
        <div
          className={`forage-cycle-period-group ${group.className}`}
          key={group.className}
        >
          {group.fields.map(renderField)}
        </div>
      ))}

      <span className="forage-cycle-remove-container">
        <IconButton
          aria-label={`${t("forageCycle:cycle-remove-period")} ${index + 1} - ${forageName}`}
          data-testid={`remove-period-${cycle.IdForragem}-${index}`}
          disabled={saving || periodCount <= 1}
          onClick={onRemove}
          className="forage-cycle-remove"
        >
          <DeleteOutline
            style={{ fontSize: 16 }}
            color={periodCount <= 1 ? "disabled" : "error"}
          />
        </IconButton>
      </span>

      {rowErrorMessages.length > 0 && (
        <div
          id={errorDescriptionId}
          className="forage-cycle-period-row__errors"
          role="alert"
        >
          <BannerAlert type="error">
            <div className="forage-cycle-period-row__error-messages">
              {rowErrorMessages.map((message) => (
                <div key={message}>{message}</div>
              ))}
            </div>
          </BannerAlert>
        </div>
      )}
    </div>
  );
}

export default ForageCyclePeriodRow;
